import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, MapPin, Sun, Moon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { companyInfo } from '../data/mock';
import { useTheme } from '../context/ThemeContext';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isDark, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '#accueil', label: 'Accueil' },
    { href: '#boutique', label: 'Boutique' },
    { href: '#services', label: 'Services' },
    { href: '#avis', label: 'Avis Google' },
    { href: '#faq', label: 'FAQ' },
    { href: '#contact', label: 'Contact' },
    { href: '/mentions-legales', label: 'Mentions légales', isRoute: true }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#accueil" className="flex items-center space-x-3">
            <img
              src={companyInfo.logoUrl}
              alt="CLIC58"
              className="h-12 w-auto dark:brightness-0 dark:invert logo-animated"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            {navLinks.map((link) => (
              link.isRoute ? (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-gray-700 dark:text-gray-200 hover:text-primary-blue dark:hover:text-primary-cyan font-medium transition-colors duration-200 relative group"
                  data-testid="header-legal-link"
                >
                  {link.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary-cyan transition-all duration-300 group-hover:w-full" />
                </Link>
              ) : (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-gray-700 dark:text-gray-200 hover:text-primary-blue dark:hover:text-primary-cyan font-medium transition-colors duration-200 relative group"
                >
                  {link.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary-cyan transition-all duration-300 group-hover:w-full" />
                </a>
              )
            ))}
          </nav>

          {/* CTA Buttons + Theme Toggle */}
          <div className="hidden lg:flex items-center space-x-3">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              aria-label="Toggle theme"
            >
              {isDark ? (
                <Sun className="w-5 h-5 text-yellow-500" />
              ) : (
                <Moon className="w-5 h-5 text-gray-600" />
              )}
            </button>
            
            <a href={companyInfo.googleMapsUrl} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-primary-blue text-primary-blue dark:text-primary-cyan dark:border-primary-cyan hover:bg-primary-blue hover:text-white rounded-full flex items-center gap-2 transition-all duration-300">
                <MapPin className="w-4 h-4" />
                Boutique
              </Button>
            </a>
            <a href={`tel:${companyInfo.phone.replace(/\./g, '')}`}>
              <Button className="bg-primary-blue hover:bg-primary-blue/90 text-white px-5 py-2 rounded-full flex items-center gap-2 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5">
                <Phone className="w-4 h-4" />
                {companyInfo.phone}
              </Button>
            </a>
          </div>

          {/* Mobile: Theme Toggle + Menu Button */}
          <div className="lg:hidden flex items-center gap-2">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              aria-label="Toggle theme"
            >
              {isDark ? (
                <Sun className="w-5 h-5 text-yellow-500" />
              ) : (
                <Moon className="w-5 h-5 text-gray-600" />
              )}
            </button>
            <button
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-gray-700 dark:text-gray-200" />
              ) : (
                <Menu className="w-6 h-6 text-gray-700 dark:text-gray-200" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-300 ${
            isMobileMenuOpen ? 'max-h-[500px] pb-6' : 'max-h-0'
          }`}
        >
          <nav className="flex flex-col space-y-3 pt-4">
            {navLinks.map((link) => (
              link.isRoute ? (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-gray-700 dark:text-gray-200 hover:text-primary-blue dark:hover:text-primary-cyan font-medium transition-colors px-2 py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-gray-700 dark:text-gray-200 hover:text-primary-blue dark:hover:text-primary-cyan font-medium transition-colors px-2 py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.label}
                </a>
              )
            ))}
            <div className="flex flex-col gap-3 pt-4">
              <a href={companyInfo.googleMapsUrl} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="w-full border-primary-blue text-primary-blue dark:text-primary-cyan dark:border-primary-cyan rounded-full flex items-center justify-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Venir en boutique
                </Button>
              </a>
              <a href={`tel:${companyInfo.phone.replace(/\./g, '')}`}>
                <Button className="w-full bg-primary-blue hover:bg-primary-blue/90 text-white rounded-full flex items-center justify-center gap-2">
                  <Phone className="w-4 h-4" />
                  {companyInfo.phone}
                </Button>
              </a>
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
